import { Menu } from '@headlessui/react';
import clsx from 'clsx';
import type { ComponentProps } from 'react';
import { useCallback, useMemo } from 'react';
import { useList } from 'react-use';

import { findFilterOptionById } from '~modules/media-library/model/FilterHelper';
import { FilterOption } from '~modules/media-library/model/FilterOption';
import {
  FilterMenuActionButton,
  FilterMenuGroupButton,
  FilterMenuItemCheckboxButton,
  FilterMenuReturnButton,
} from '~modules/media-library/view/FilterDropdown/FilterMenuButtons';
import { getLastItem } from '~utils/array';

function isItemSelected(selectedIds: Array<FilterOption['id']>, id: FilterOption['id']) {
  return selectedIds.includes(id);
}

function isFilterGroupsInvolved(ids: Array<FilterOption['id']>, id: FilterOption['id']) {
  return ids.includes(id);
}

type FilterMenuPanelProps = {
  items: FilterOption[];
  onItemClick?: (id: FilterOption['id'], selected: boolean) => void;
  onClear?: ComponentProps<'button'>['onClick'];
  labelClear?: string;
  selected?: Array<FilterOption['id']>;
  involvedFilterGroups?: Array<FilterOption['id']>;
};

export function FilterMenuPanel({
  items,
  onItemClick,
  onClear,
  labelClear,
  selected = [],
  involvedFilterGroups = [],
}: FilterMenuPanelProps) {
  // list of parentIds to go back and forth in the dropdown
  const [parentIdList, { push: pushToParentIdList, removeAt: removeFromParentIdList }] = useList<string>([]);

  // Handler: click on parent (go back one level)
  const handleParentClick = useCallback<ComponentProps<'button'>['onClick']>(
    evt => {
      // Stop event bubble because that would close the dropdown
      evt.stopPropagation();
      evt.preventDefault();

      // remove from parent list
      removeFromParentIdList(parentIdList.length - 1);
    },
    [parentIdList.length, removeFromParentIdList],
  );

  // Handler: click on item (either select it or go one level down)
  const makeHandleItemClickCheckbox = useCallback(
    (id: FilterOption['id']): ComponentProps<'button'>['onClick'] =>
      evt => {
        // get the item
        const item = findFilterOptionById(items, id);
        if (!item) return;

        // Stop event bubble because that would close the dropdown
        evt.stopPropagation();
        evt.preventDefault();

        onItemClick?.(id, isItemSelected(selected, id));
      },
    [onItemClick, items, selected],
  );

  const makeHandleItemClickGroup = useCallback(
    (id: FilterOption['id']): ComponentProps<'button'>['onClick'] =>
      evt => {
        // get the item
        const item = findFilterOptionById(items, id);
        if (!item) return;

        // Stop event bubble because that would close the dropdown
        evt.stopPropagation();
        evt.preventDefault();

        // if it is a parent, add it to the parent list
        if (item.children && item.children.length > 0) {
          pushToParentIdList(item.id);
          return;
        }
      },
    [items, pushToParentIdList],
  );

  // Get the current parent based on the parentIdList and update when items (prop) changes
  const currentParent = useMemo(() => {
    const latestParentId = getLastItem(parentIdList);
    if (!latestParentId) return undefined;
    const item = findFilterOptionById(items, latestParentId);
    return item;
  }, [parentIdList, items]);

  // Derived list of items to display
  const itemsList = currentParent?.children || items;

  return (
    <div className="ml--filter-menu rounded bg-white shadow-xl flex flex-col w-full">
      {!!currentParent && (
        <Menu.Item>
          {({ active }) => (
            <FilterMenuReturnButton focus={active} onClick={handleParentClick}>
              {currentParent.label}
            </FilterMenuReturnButton>
          )}
        </Menu.Item>
      )}
      <div className="flex flex-col gap-1 p-3">
        {itemsList.map(option => (
          <Menu.Item key={option.id}>
            {({ active }) => {
              if (option.children && option.children.length > 0) {
                return (
                  <div className="flex">
                    <FilterMenuItemCheckboxButton
                      data-value={option.id}
                      selected={isItemSelected(selected, option.id)}
                      onClick={makeHandleItemClickCheckbox(option.id)}
                    ></FilterMenuItemCheckboxButton>
                    <FilterMenuGroupButton
                      focus={active}
                      active={isFilterGroupsInvolved(involvedFilterGroups, option.id)}
                      disabled={isItemSelected(selected, option.id)}
                      onClick={makeHandleItemClickGroup(option.id)}
                      data-value={option.id}
                    >
                      {option.label}
                    </FilterMenuGroupButton>
                  </div>
                );
              }
              return (
                <div className="flex">
                  <FilterMenuItemCheckboxButton
                    data-value={option.id}
                    selected={isItemSelected(selected, option.id)}
                    onClick={makeHandleItemClickCheckbox(option.id)}
                  ></FilterMenuItemCheckboxButton>
                  <span
                    className={clsx(
                      'text-black font-sans underline-offset-4 text-sm w-full pt-1',
                      isItemSelected(selected, option.id) && 'font-semibold',
                    )}
                  >
                    {option.label}
                  </span>
                </div>
              );
            }}
          </Menu.Item>
        ))}
        {!currentParent && !!labelClear && (
          <Menu.Item as="div" className="text-right leading-none">
            {({ active }) => (
              <FilterMenuActionButton focus={active} onClick={onClear}>
                {labelClear}
              </FilterMenuActionButton>
            )}
          </Menu.Item>
        )}
      </div>
    </div>
  );
}
